import React from "react";
import styles from "./OneTickField.module.css";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveTemplate(
  template: string,
  productTitle: string,
  priceLabel: string
): string {
  return template
    .replace(/\{product_name\}/g, productTitle)
    .replace(/\{title\}/g, productTitle)
    .replace(/\{price\}/g, priceLabel);
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OneTickFieldStyles {
  backgroundColor?: string;
  borderStyle?: "solid" | "dashed" | "dotted" | "none";
  borderWidth?: number;
  borderColor?: string;
  borderRadius?: number;
}

export interface OneTickFieldProps {
  textTemplate: string;
  productTitle?: string;
  priceLabel?: string;
  description?: string;
  imageUrl?: string;
  textColor?: string;
  descriptionColor?: string;
  styles?: OneTickFieldStyles;
  selected: boolean;
  onToggle: (next: boolean) => void;
  disabled?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const OneTickField: React.FC<OneTickFieldProps> = ({
  textTemplate,
  productTitle = "",
  priceLabel = "",
  description,
  imageUrl,
  textColor,
  descriptionColor,
  styles: schemaStyles,
  selected,
  onToggle,
  disabled = false,
}) => {
  const text = resolveTemplate(textTemplate, productTitle, priceLabel);

  const containerStyle: React.CSSProperties = {
    backgroundColor: schemaStyles?.backgroundColor,
    borderStyle: schemaStyles?.borderStyle ?? "solid",
    borderWidth:
      schemaStyles?.borderWidth !== undefined
        ? `${schemaStyles.borderWidth}px`
        : undefined,
    borderColor: schemaStyles?.borderColor,
    borderRadius:
      schemaStyles?.borderRadius !== undefined
        ? `${schemaStyles.borderRadius}px`
        : undefined,
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    onToggle(e.target.checked);
  };

  return (
    <label
      className={styles.container}
      style={containerStyle}
      data-disabled={disabled || undefined}
    >
      <input
        type="checkbox"
        className={styles.checkbox}
        checked={selected}
        onChange={handleChange}
        disabled={disabled}
        aria-label={text}
      />

      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          className={styles.image}
          aria-hidden="true"
        />
      )}

      <div className={styles.content}>
        <span className={styles.text} style={{ color: textColor }}>
          {text}
        </span>
        {description && description.trim() !== "" && (
          <span
            className={styles.description}
            style={{ color: descriptionColor }}
          >
            {description}
          </span>
        )}
      </div>
    </label>
  );
};
